import React, { useState, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import { FiX, FiSearch, FiCheck, FiArrowLeft, FiArrowRight, FiChevronDown } from "react-icons/fi";
import { integrationsService } from "../../services/integrationsService";
import { ProviderOptions, DataSelections, ProviderOption } from "../../types/integrationOptions";
import InlineSpinner from "../common/InlineSpinner";
import IntegrationLogo from "../common/IntegrationLogo";

// Type-safe icon wrappers
const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;
const SearchIcon = FiSearch as React.FC<React.SVGProps<SVGSVGElement>>;
const CheckIcon = FiCheck as React.FC<React.SVGProps<SVGSVGElement>>;
const ArrowLeftIcon = FiArrowLeft as React.FC<React.SVGProps<SVGSVGElement>>;
const ArrowRightIcon = FiArrowRight as React.FC<React.SVGProps<SVGSVGElement>>;
const ChevronDownIcon = FiChevronDown as React.FC<React.SVGProps<SVGSVGElement>>;

interface IntegrationDataSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (selections: DataSelections) => void;
  provider: string;
  providerName: string;
}

const PROVIDER_CONFIG: Record<string, {
  steps: { id: string; title: string; subtitle: string }[];
  itemsLabel: string;
  supportsSearch: boolean;
}> = {
  github: {
    steps: [
      { id: 'repos', title: 'Select Repositories', subtitle: 'Choose which repositories to connect to SendUpdates.ai' },
      { id: 'data', title: 'Select Data Types', subtitle: 'Choose what kind of information you want us to analyze' }
    ],
    itemsLabel: 'Repositories',
    supportsSearch: true
  },
  gitlab: {
    steps: [
      { id: 'projects', title: 'Select Projects', subtitle: 'Choose which projects to connect to SendUpdates.ai' },
      { id: 'data', title: 'Select Data Types', subtitle: 'Choose what kind of information you want us to analyze' }
    ],
    itemsLabel: 'Projects',
    supportsSearch: true
  },
  slack: {
    steps: [
      { id: 'channels', title: 'Select Channels', subtitle: 'Choose which channels SendUpdates.ai should monitor' },
      { id: 'data', title: 'Select Data Types', subtitle: 'Choose what kind of messages you want us to analyze' }
    ],
    itemsLabel: 'Channels',
    supportsSearch: true
  },
  jira: {
    steps: [
      { id: 'sites', title: 'Select Sites', subtitle: 'Choose which Jira sites to connect to SendUpdates.ai' },
      { id: 'data', title: 'Select Data Types', subtitle: 'Choose what kind of tickets you want us to analyze' }
    ],
    itemsLabel: 'Sites',
    supportsSearch: true
  },
  linear: {
    steps: [
      { id: 'structures', title: 'Select Teams, Projects & Cycles', subtitle: 'Choose which Linear structures to connect to SendUpdates.ai' },
      { id: 'data', title: 'Select Data Types', subtitle: 'Choose what kind of information you want us to analyze' }
    ],
    itemsLabel: 'items',
    supportsSearch: true
  },
  hubspot: {
    steps: [
      { id: 'data', title: 'Select CRM Data', subtitle: 'Choose what kind of HubSpot data you want us to analyze' }
    ],
    itemsLabel: 'Data Types',
    supportsSearch: false
  },
  zendesk: {
    steps: [
      { id: 'views', title: 'Select Ticket Views', subtitle: 'Choose which ticket views SendUpdates.ai should monitor' },
      { id: 'data', title: 'Select Data Types', subtitle: 'Choose what support data you want us to analyze' }
    ],
    itemsLabel: 'Ticket Views',
    supportsSearch: true
  },
  intercom: {
    steps: [
      { id: 'inboxes', title: 'Select Inboxes', subtitle: 'Choose which inboxes/teams SendUpdates.ai should monitor' },
      { id: 'data', title: 'Select Data Types', subtitle: 'Choose what conversation data you want us to analyze' }
    ],
    itemsLabel: 'Inboxes/Teams',
    supportsSearch: true
  },
  'google-drive': {
    steps: [
      { 
        id: 'folders', 
        title: 'Select Folders', 
        subtitle: 'Choose which folders SendUpdates.ai can access for business updates' 
      },
      { 
        id: 'data', 
        title: 'Select File Types', 
        subtitle: 'Choose what types of documents you want us to analyze' 
      }
    ],
    itemsLabel: 'Folders',
    supportsSearch: true
  },
  pipedrive: {
    steps: [
      {
        id: 'data',
        title: 'Select CRM Data',
        subtitle: 'Choose what kind of Pipedrive data you want us to analyze'
      }
    ],
    itemsLabel: 'Data Types',
    supportsSearch: false
  },
  asana: {
    steps: [
      { id: 'projects', title: 'Select Projects', subtitle: 'Choose which Asana projects SendUpdates.ai should monitor' },
      { id: 'data', title: 'Select Data Types', subtitle: 'Choose what kind of project data you want us to analyze' }
    ],
    itemsLabel: 'Projects',
    supportsSearch: true
  },
  monday: {
    steps: [
      { id: 'boards', title: 'Select Boards', subtitle: 'Choose which Monday.com boards SendUpdates.ai should monitor' },
      { id: 'data', title: 'Select Data Types', subtitle: 'Choose what kind of board data you want us to analyze' }
    ],
    itemsLabel: 'Boards',
    supportsSearch: true
  },
  clickup: {
    steps: [
      { id: 'data', title: 'Select Data Types', subtitle: 'Choose what kind of ClickUp data you want us to analyze' }
    ],
    itemsLabel: 'Data Types',
    supportsSearch: false
  },
  confluence: {
    steps: [
      { id: 'spaces', title: 'Select Spaces', subtitle: 'Choose which Confluence spaces SendUpdates.ai should monitor' },
      { id: 'data', title: 'Select Data Types', subtitle: 'Choose what kind of content you want us to analyze' }
    ],
    itemsLabel: 'Spaces',
    supportsSearch: true
  },
  'google-analytics': {
    steps: [
      { id: 'properties', title: 'Select Properties', subtitle: 'Choose which Google Analytics properties to connect to SendUpdates.ai' },
      { id: 'data', title: 'Select Analytics Data', subtitle: 'Choose what kind of Google Analytics data you want us to analyze' }
    ],
    itemsLabel: 'Properties',
    supportsSearch: true
  },
  'google-sheets': {
    steps: [
      { id: 'spreadsheets', title: 'Select Spreadsheets', subtitle: 'Choose which spreadsheets SendUpdates.ai should access' },
      { id: 'data', title: 'Select Data Types', subtitle: 'Choose what kind of spreadsheet data you want us to analyze' }
    ],
    itemsLabel: 'Spreadsheets',
    supportsSearch: true
  },
  gmail: {
    steps: [
      { id: 'labels', title: 'Select Labels', subtitle: 'Choose which Gmail labels SendUpdates.ai should monitor for updates' }
    ],
    itemsLabel: 'Labels',
    supportsSearch: true
  },
  'google-calendar': {
    steps: [
      { id: 'calendars', title: 'Select Calendars', subtitle: 'Choose which calendars SendUpdates.ai should monitor' },
      { id: 'data', title: 'Select Data Types', subtitle: 'Choose what kind of calendar data you want us to analyze' }
    ],
    itemsLabel: 'Calendars',
    supportsSearch: true
  },
  'google-ads': {
    steps: [
      { id: 'data', title: 'Select Ads Data', subtitle: 'Choose what kind of Google Ads data you want us to analyze' }
    ],
    itemsLabel: 'Data Types',
    supportsSearch: false
  },
  figma: {
    steps: [
      { id: 'data', title: 'Select Design Data', subtitle: 'Choose what kind of Figma data you want us to analyze' }
    ],
    itemsLabel: 'Data Types',
    supportsSearch: false
  },
  close: {
    steps: [
      { id: 'data', title: 'Select CRM Data', subtitle: 'Choose what kind of Close data you want us to analyze' }
    ],
    itemsLabel: 'Data Types',
    supportsSearch: false
  },
  miro: {
    steps: [
      { id: 'boards', title: 'Select Boards', subtitle: 'Choose which Miro boards SendUpdates.ai should monitor' },
      { id: 'data', title: 'Select Data Types', subtitle: 'Choose what kind of board data you want us to analyze' }
    ],
    itemsLabel: 'Boards',
    supportsSearch: true
  },
  zapier: {
    steps: [
      { id: 'data', title: 'Select Automation Data', subtitle: 'Choose what kind of Zapier data you want us to analyze' }
    ],
    itemsLabel: 'Data Types',
    supportsSearch: false
  }
};

const IntegrationDataSelectionModal: React.FC<IntegrationDataSelectionModalProps> = ({
  isOpen,
  onClose,
  onSave,
  provider,
  providerName,
}) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [options, setOptions] = useState<ProviderOptions | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedKinds, setSelectedKinds] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [showOrgAccessInfo, setShowOrgAccessInfo] = useState(false);
  const [orgAccessInfoExpanded, setOrgAccessInfoExpanded] = useState(false);

  const config = PROVIDER_CONFIG[provider.toLowerCase()] || {
    steps: [
      { id: 'items', title: 'Select Items', subtitle: 'Choose items to connect' },
      { id: 'data', title: 'Select Data Types', subtitle: 'Choose what data to share' }
    ],
    itemsLabel: 'Items',
    supportsSearch: true
  };

  const effectiveSteps = useMemo(() => {
    if (options?.type === 'none') {
      return [{ id: 'data', title: 'Select Data Types', subtitle: 'Choose what kind of information you want us to analyze' }];
    }
    return config.steps;
  }, [options?.type, config.steps]);

  useEffect(() => {
    if (isOpen && provider) {
      loadOptions();
    } else {
      // Reset state when closing
      setStep(1);
      setLoading(true);
      setError(null);
      setOptions(null);
      setSelectedIds(new Set());
      setSelectedKinds(new Set());
      setSearchTerm("");
    }
  }, [isOpen, provider]);

  const loadOptions = async () => {
    try {
      setLoading(true);
      setError(null);
      const opt = await integrationsService.getProviderOptions(provider);
      setOptions(opt as ProviderOptions);

      // Check for API-level errors (e.g., Slack API returned an error)
      if ((opt as any).error) {
        console.error(`[${provider}] API error:`, (opt as any).error);
        setError((opt as any).error);
        return;
      }

      // Check for empty items when we expect a list
      if (opt.type === 'list' && (!opt.items || opt.items.length === 0)) {
        const providerLabel = config.itemsLabel.toLowerCase();
        setError(`No ${providerLabel} found. Please check that your ${providerName} account has ${providerLabel} and that you've granted the necessary permissions. Try reconnecting the integration if the issue persists.`);
        return;
      }

      // Default selections
      if (opt.kinds) {
        // Pre-select some common kinds if available
        const defaultKinds = opt.kinds
          .filter(k => ["commits", "pull_requests", "issues", "messages"].includes(k.id))
          .map(k => k.id);

        if (defaultKinds.length > 0) {
          setSelectedKinds(new Set(defaultKinds));
        } else if (opt.kinds.length > 0) {
          // Otherwise select the first one
          setSelectedKinds(new Set([opt.kinds[0].id]));
        }
      }

      // Detect missing organization repos for GitHub
      if (provider.toLowerCase() === 'github' && opt.type === 'list' && opt.items && opt.items.length > 0) {
        // Check if all repos appear to be personal (not from organizations)
        // Org repos typically have format: "org-name/repo-name" where org-name is not the user's username
        const hasOrgIndicators = opt.items.some((item: any) => {
          // Check if the repo name suggests it's from an organization
          // This is a heuristic since we don't have owner type in the items
          const parts = item.id.split('/');
          return parts.length === 2 && parts[0] !== parts[0].toLowerCase();
        });

        if (!hasOrgIndicators) {
          // Likely no org repos - show info banner
          setShowOrgAccessInfo(true);
        }
      }
    } catch (err: any) {
      console.error("Failed to load options:", err);
      setError(err?.message || "Failed to load integration options. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const toggleId = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  };

  const toggleKind = (id: string) => {
    const next = new Set(selectedKinds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedKinds(next);
  };

  const handleNext = () => {
    if (step === 1 && options?.type === 'list' && selectedIds.size === 0) {
      setError(`Please select at least one ${config.itemsLabel.toLowerCase().slice(0, -1)}.`);
      return;
    }
    setError(null);
    if (step < effectiveSteps.length) {
      setStep(step + 1);
    } else {
      handleSave();
    }
  };

  const handleBack = () => {
    setError(null);
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSave = async () => {
    if (selectedKinds.size === 0 && (options?.kinds?.length || 0) > 0) {
      setError("Please select at least one data type.");
      return;
    }

    // Build ID→name map from loaded options
    const idToNameMap: Record<string, string> = {};
    if (options?.items) {
      for (const item of options.items) {
        if (selectedIds.has(item.id)) {
          idToNameMap[item.id] = item.name;
        }
      }
    }

    setSaving(true);
    setError(null);
    try {
      await onSave({
        selectedIds: Array.from(selectedIds),
        selectedKinds: Array.from(selectedKinds),
        idToNameMap,
      });
      // Success! Parent component will handle closing the modal
    } catch (err: any) {
      console.error('Failed to save selections:', err);
      setError(err?.message || "Failed to save selections.");
    } finally {
      // Always reset saving state
      setSaving(false);
    }
  };

  const filteredItems = useMemo(() => {
    if (!options?.items) return [];
    if (!searchTerm) return options.items;
    return options.items.filter(item => 
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.id.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options?.items, searchTerm]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !saving) {
      onClose();
    }
  };

  const currentStepConfig = effectiveSteps[step - 1];

  return ReactDOM.createPortal(
    <div className="su-modal-overlay" onClick={handleBackdropClick}>
      <div className="su-modal" style={{ maxWidth: "600px", width: "95%" }}>
        {/* Header */}
        <div className="su-modal-header">
          <div className="su-modal-title" style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <IntegrationLogo name={providerName} className="w-6 h-6" />
            <span style={{ textTransform: "uppercase", letterSpacing: "1px" }}>{providerName} SETUP</span>
          </div>
          {!saving && (
            <button type="button" className="su-modal-close" onClick={onClose}>
              <CloseIcon />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="su-modal-body" style={{ padding: "24px", minHeight: "350px", display: "flex", flexDirection: "column" }}>
          {loading ? (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "16px" }}>
              <InlineSpinner />
              <p style={{ color: "#9CA3AF", fontSize: "14px" }}>Fetching available {config.itemsLabel.toLowerCase()}...</p>
            </div>
          ) : error ? (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
              <div style={{ color: "#EF4444", marginBottom: "16px", fontSize: "48px" }}>⚠️</div>
              <p style={{ color: "#EF4444", marginBottom: "24px" }}>{error}</p>
              <button className="su-btn-secondary" onClick={loadOptions}>Retry</button>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: "24px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                  <h2 style={{ fontSize: "18px", fontWeight: 600, color: "white", margin: 0 }}>{currentStepConfig.title}</h2>
                  <span style={{ fontSize: "12px", color: "#6B7280", fontWeight: 600 }}>STEP {step} OF {effectiveSteps.length}</span>
                </div>
                <p style={{ fontSize: "14px", color: "#9CA3AF", margin: 0 }}>{currentStepConfig.subtitle}</p>
              </div>

              {step === 1 && options?.type === 'list' && (
                <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                  {/* GitHub: Show organization access info if no org repos detected */}
                  {provider.toLowerCase() === 'github' && showOrgAccessInfo && (
                    <div style={{
                      marginBottom: "16px",
                      backgroundColor: "#1e293b",
                      border: "1px solid #3b82f6",
                      borderRadius: "8px",
                      overflow: "hidden",
                    }}>
                      {/* Collapsible Header */}
                      <div 
                        onClick={() => setOrgAccessInfoExpanded(!orgAccessInfoExpanded)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          padding: "16px",
                          cursor: "pointer",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#1e3a5f";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        <div style={{ fontSize: "24px", flexShrink: 0 }}>💡</div>
                        <div style={{ flex: 1, color: "#60a5fa", fontSize: "14px", fontWeight: 600 }}>
                          Don&apos;t see your organization repositories?
                        </div>
                        <ChevronDownIcon 
                          style={{
                            width: "20px",
                            height: "20px",
                            color: "#60a5fa",
                            transform: orgAccessInfoExpanded ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "transform 0.2s ease",
                            flexShrink: 0,
                          }}
                        />
                      </div>
                      
                      {/* Expandable Content */}
                      {orgAccessInfoExpanded && (
                        <div style={{ padding: "0 16px 16px 16px" }}>
                          <div style={{ paddingLeft: "36px" }}>
                            <div style={{ color: "#94a3b8", fontSize: "13px", lineHeight: "1.6", marginBottom: "12px" }}>
                              If you&apos;re a member of GitHub organizations, you may need to grant organization access:
                            </div>
                            <ol style={{ margin: "0 0 12px 0", paddingLeft: "20px", color: "#cbd5e1", fontSize: "13px", lineHeight: "1.8", listStyleType: "decimal", listStylePosition: "outside" }}>
                              <li style={{ display: "list-item", marginBottom: "4px" }}>Visit <a href="https://github.com/settings/connections/applications" target="_blank" rel="noopener noreferrer" style={{ color: "#3b82f6", textDecoration: "underline" }}>GitHub App Settings</a></li>
                              <li style={{ display: "list-item", marginBottom: "4px" }}>Find &quot;SendUpdates.ai&quot;</li>
                              <li style={{ display: "list-item", marginBottom: "4px" }}>Click <strong>&quot;Grant&quot;</strong> or <strong>&quot;Request&quot;</strong> next to each organization</li>
                              <li style={{ display: "list-item", marginBottom: "4px" }}>If you are not the owner of the organization, you may need to ask the owner to grant access.</li>
                              <li style={{ display: "list-item", marginBottom: "4px" }}>Come back and refresh this page</li>
                            </ol>
                            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOrgAccessInfoExpanded(false);
                                  setShowOrgAccessInfo(false);
                                }}
                                style={{
                                  padding: "6px 12px",
                                  fontSize: "12px",
                                  backgroundColor: "transparent",
                                  border: "1px solid #475569",
                                  borderRadius: "4px",
                                  color: "#94a3b8",
                                  cursor: "pointer",
                                  transition: "all 0.2s"
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.borderColor = "#64748b";
                                  e.currentTarget.style.color = "#cbd5e1";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.borderColor = "#475569";
                                  e.currentTarget.style.color = "#94a3b8";
                                }}
                              >
                                Dismiss
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  loadOptions();
                                }}
                                style={{
                                  padding: "6px 12px",
                                  fontSize: "12px",
                                  backgroundColor: "#3b82f6",
                                  border: "1px solid #3b82f6",
                                  borderRadius: "4px",
                                  color: "white",
                                  cursor: "pointer",
                                  transition: "all 0.2s"
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = "#2563eb";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = "#3b82f6";
                                }}
                              >
                                Refresh Repositories
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Show workspace name for Slack so users can verify they're connected to the right workspace */}
                  {provider.toLowerCase() === 'slack' && (options as any).workspace && (
                    <div style={{
                      marginBottom: "16px",
                      padding: "12px 16px",
                      backgroundColor: "#1e1b4b",
                      border: "1px solid #5b21b6",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px"
                    }}>
                      <span style={{ color: "#a78bfa", fontSize: "14px" }}>Connected to workspace:</span>
                      <span style={{ color: "white", fontWeight: 600, fontSize: "14px" }}>{(options as any).workspace}</span>
                      <span style={{ color: "#6B7280", fontSize: "12px", marginLeft: "auto" }}>
                        Wrong workspace? Reconnect Slack.
                      </span>
                    </div>
                  )}

                  {/* Show helpful info for Linear about different structure types */}
                  {provider.toLowerCase() === 'linear' && filteredItems.length > 0 && (
                    <div style={{
                      marginBottom: "16px",
                      padding: "12px 16px",
                      backgroundColor: "#0f172a",
                      border: "1px solid #334155",
                      borderRadius: "8px",
                    }}>
                      <div style={{ fontSize: "13px", color: "#cbd5e1", lineHeight: "1.6" }}>
                        <span style={{ fontWeight: 600, color: "#e2e8f0" }}>💡 Multiple structure types:</span>
                        <span style={{ marginLeft: "8px", color: "#93c5fd" }}>Teams</span> • 
                        <span style={{ marginLeft: "4px", color: "#fdba74" }}>Projects</span> • 
                        <span style={{ marginLeft: "4px", color: "#c4b5fd" }}>Cycles</span>
                        <span style={{ marginLeft: "8px", color: "#94a3b8" }}>— Select any combination that&apos;s relevant to your updates.</span>
                      </div>
                    </div>
                  )}

                  {/* Show helpful info for Google Drive about folder selection */}
                  {provider.toLowerCase() === 'google-drive' && (
                    <div style={{
                      marginBottom: "16px",
                      padding: "12px 16px",
                      backgroundColor: "#1e1b4b",
                      border: "1px solid #5b21b6",
                      borderRadius: "8px",
                    }}>
                      <div style={{ fontSize: "13px", color: "#cbd5e1", lineHeight: "1.6" }}>
                        <span style={{ fontWeight: 600, color: "#e2e8f0" }}>💡 Tip:</span>
                        <span style={{ marginLeft: "8px", color: "#c4b5fd" }}>
                          Select &quot;My Drive&quot; to access all folders, or choose specific folders for more control.
                          We&apos;ll only analyze documents you select for business updates.
                        </span>
                      </div>
                    </div>
                  )}

                  {config.supportsSearch && (
                    <div style={{ position: "relative", marginBottom: "16px" }}>
                      <SearchIcon style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#6B7280" }} />
                      <input
                        type="text"
                        placeholder={`Search ${config.itemsLabel.toLowerCase()}...`}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "10px 12px 10px 40px",
                          backgroundColor: "#1F2937",
                          border: "1px solid #374151",
                          borderRadius: "6px",
                          color: "white",
                          fontSize: "14px"
                        }}
                      />
                    </div>
                  )}

                  <div style={{ 
                    flex: 1, 
                    overflowY: "auto", 
                    maxHeight: "300px", 
                    border: "1px solid #374151", 
                    borderRadius: "6px",
                    backgroundColor: "#111827"
                  }}>
                    {filteredItems.length === 0 ? (
                      <div style={{ padding: "40px", textAlign: "center", color: "#6B7280" }}>
                        {searchTerm ? "No matches found" : `No ${config.itemsLabel.toLowerCase()} available`}
                      </div>
                    ) : (
                      filteredItems.map(item => {
                        // Type badge colors for Linear items
                        const typeBadgeColors: Record<string, { bg: string; text: string; border: string }> = {
                          team: { bg: "#1e3a8a", text: "#93c5fd", border: "#3b82f6" },
                          project: { bg: "#7c2d12", text: "#fdba74", border: "#f97316" },
                          cycle: { bg: "#4c1d95", text: "#c4b5fd", border: "#a855f7" },
                        };
                        
                        const itemType = (item as any).type;
                        const typeLabel = (item as any).typeLabel;
                        const badgeStyle = itemType && typeBadgeColors[itemType];
                        
                        return (
                          <label 
                            key={item.id} 
                            style={{ 
                              display: "flex", 
                              alignItems: "center", 
                              padding: "12px 16px", 
                              cursor: "pointer",
                              borderBottom: "1px solid #1F2937",
                              transition: "background-color 0.2s"
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#1F2937"}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                          >
                            <input
                              type="checkbox"
                              checked={selectedIds.has(item.id)}
                              onChange={() => toggleId(item.id)}
                              style={{ marginRight: "12px", width: "18px", height: "18px", accentColor: "#ae7aff" }}
                            />
                            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <span style={{ color: "white", fontSize: "14px", fontWeight: 500 }}>{item.name}</span>
                                {badgeStyle && typeLabel && (
                                  <span style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    padding: "2px 8px",
                                    fontSize: "11px",
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                    backgroundColor: badgeStyle.bg,
                                    color: badgeStyle.text,
                                    border: `1px solid ${badgeStyle.border}`,
                                    borderRadius: "4px",
                                  }}>
                                    {typeLabel}
                                  </span>
                                )}
                              </div>
                              {item.description && <span style={{ color: "#6B7280", fontSize: "12px", marginTop: "2px" }}>{item.description}</span>}
                            </div>
                          </label>
                        );
                      })
                    )}
                  </div>
                  <div style={{ marginTop: "12px", fontSize: "12px", color: "#6B7280", textAlign: "right" }}>
                    {selectedIds.size} selected
                  </div>
                </div>
              )}

              {((step === 1 && options?.type === 'none') || step === 2) && (
                <div style={{ flex: 1 }}>
                  {/* Show helpful info for Pipedrive about CRM data types */}
                  {provider.toLowerCase() === 'pipedrive' && (
                    <div style={{
                      marginBottom: "16px",
                      padding: "12px 16px",
                      backgroundColor: "#0f172a",
                      border: "1px solid #334155",
                      borderRadius: "8px",
                    }}>
                      <div style={{ fontSize: "13px", color: "#cbd5e1", lineHeight: "1.6" }}>
                        <span style={{ fontWeight: 600, color: "#e2e8f0" }}>💡 Recommended:</span>
                        <span style={{ marginLeft: "8px", color: "#93c5fd" }}>
                          Select &quot;Deals&quot; and &quot;Activities&quot; for comprehensive business updates.
                          You can always change these selections later in Settings.
                        </span>
                      </div>
                    </div>
                  )}
                  <div style={{ 
                    display: "grid", 
                    gridTemplateColumns: "1fr 1fr", 
                    gap: "12px" 
                  }}>
                    {options?.kinds?.map(kind => (
                      <label 
                        key={kind.id} 
                        style={{ 
                          display: "flex", 
                          alignItems: "flex-start", 
                          padding: "16px", 
                          backgroundColor: selectedKinds.has(kind.id) ? "rgba(174, 122, 255, 0.1)" : "#1F2937",
                          border: `1px solid ${selectedKinds.has(kind.id) ? "#ae7aff" : "#374151"}`,
                          borderRadius: "8px",
                          cursor: "pointer",
                          transition: "all 0.2s"
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedKinds.has(kind.id)}
                          onChange={() => toggleKind(kind.id)}
                          style={{ marginTop: "3px", marginRight: "12px", width: "16px", height: "16px", accentColor: "#ae7aff" }}
                        />
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <span style={{ color: "white", fontSize: "14px", fontWeight: 600 }}>{kind.name}</span>
                          {kind.description && <span style={{ color: "#9CA3AF", fontSize: "12px", marginTop: "4px" }}>{kind.description}</span>}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="su-modal-footer" style={{ justifyContent: "space-between", padding: "16px 24px" }}>
          <div>
            {!loading && !error && step > 1 && (
              <button
                type="button"
                className="su-btn-secondary"
                onClick={handleBack}
                disabled={saving}
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <ArrowLeftIcon /> Back
              </button>
            )}
          </div>
          <div style={{ display: "flex", gap: "12px" }}>
            <button
              type="button"
              className="su-btn-secondary"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleNext}
              disabled={loading || !!error || saving}
              className="su-btn-primary"
              style={{ 
                display: "flex",
                alignItems: "center",
                gap: "8px",
                minWidth: "100px",
                justifyContent: "center"
              }}
            >
              {saving ? (
                <InlineSpinner />
              ) : step === effectiveSteps.length ? (
                <>Complete <CheckIcon /></>
              ) : (
                <>Next <ArrowRightIcon /></>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default IntegrationDataSelectionModal;

